import {
  InputBase,
  InputLabel,
  Stack,
  SxProps,
  Theme,
  Typography,
  useTheme,
} from "@mui/material";
import { SetStateAction } from "jotai";
import { CustomToggleButton } from "library/base";
import { Dispatch, useEffect, useRef } from "react";
import CrownIcon from "assets/crown.svg";
import { Button } from "components";
import { NewUser } from "../../types";
import { validateNewUserInputAndAddToList } from "./helpers";

export const AddNewUser = ({
  email,
  setEmail,
  fullName,
  setFullName,
  isPrivileged,
  setIsPrivileged,
  newUsers,
  setNewUsers,
  addNewUserError,
  setAddNewUserError,
}: {
  email: string;
  setEmail: Dispatch<SetStateAction<string>>;
  fullName: string;
  setFullName: Dispatch<SetStateAction<string>>;
  isPrivileged: boolean;
  setIsPrivileged: Dispatch<SetStateAction<boolean>>;
  newUsers: NewUser[];
  setNewUsers: Dispatch<SetStateAction<NewUser[]>>;
  addNewUserError: string;
  setAddNewUserError: Dispatch<SetStateAction<string>>;
}) => {
  const theme = useTheme();

  const mailIdRef = useRef<HTMLInputElement | null>(null);

  const onAddClick = () => {
    validateNewUserInputAndAddToList({
      fullName: fullName,
      setFullName: setFullName,
      email: email,
      setEmail: setEmail,
      newUsersInDrawer: newUsers,
      setNewUsersInDrawer: setNewUsers,
      setAddNewUserError: setAddNewUserError,
      isUserPrivileged: isPrivileged,
    });
  };

  useEffect(() => {
    if (!email && addNewUserError) {
      setAddNewUserError("");
    }
  }, [email]);

  return (
    <>
      <Stack gap="4px">
        <Stack
          direction="row"
          alignItems={"center"}
          justifyContent={"flex-start"}
          gap="8px"
          py="12px"
          pr="16px"
          bgcolor={theme.palette.neutral["005"]}
          borderRadius="4px"
          border={
            addNewUserError ? `1px solid ${theme.palette.error.main}` : "none"
          }
        >
          <Stack flexDirection={"row"} alignItems={"center"} flexGrow={1}>
            <InputComponent
              label="Full Name"
              value={fullName}
              setValue={setFullName}
              placeholder="Enter full name"
              autoFocus={true}
              sx={{
                borderRight: `1px solid ${theme.palette.neutral["020"]}`,
              }}
              onKeyDown={(e: KeyboardEvent) => {
                if (e.key === "Enter" && fullName.trim().length > 0) {
                  if (mailIdRef.current) {
                    mailIdRef.current.focus();
                  }
                }
              }}
            />
            <InputComponent
              label="Mail ID"
              value={email}
              setValue={setEmail}
              placeholder="Enter mail id"
              inputRef={mailIdRef}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onAddClick();
                }
              }}
            />
          </Stack>
        </Stack>
        {addNewUserError && (
          <Typography variant="body-small" color={"error.main"} pl="8px">
            {addNewUserError}
          </Typography>
        )}
      </Stack>
      <UserPrivilegeToggleAndAddButton
        isPrivileged={isPrivileged}
        setIsPrivileged={setIsPrivileged}
        onAddClick={onAddClick}
        isAddDisabled={!fullName || !email}
      />
    </>
  );
};

const UserPrivilegeToggleAndAddButton = ({
  isPrivileged,
  setIsPrivileged,
  onAddClick,
  isAddDisabled,
}: {
  isPrivileged: boolean;
  setIsPrivileged: Dispatch<SetStateAction<boolean>>;
  onAddClick: () => void;
  isAddDisabled: boolean;
}) => {
  return (
    <Stack direction={"row"} justifyContent={"space-between"}>
      <Stack direction={"row"} gap={"8px"} sx={{ alignItems: "center" }}>
        <CustomToggleButton
          name={"is_active"}
          isChecked={isPrivileged}
          isDisabled={false}
          onChange={() => {
            setIsPrivileged((prev) => !prev);
          }}
        />
        <img
          src={CrownIcon}
          alt="crown"
          style={{ width: "20px", height: "20px" }}
        />
        <Typography variant="body-medium">Privileged user</Typography>
      </Stack>
      <Button variant="primary" onClick={onAddClick} disabled={isAddDisabled}>
        Add
      </Button>
    </Stack>
  );
};

const InputComponent = ({
  label,
  value,
  setValue,
  placeholder,
  autoFocus = false,
  sx,
  onKeyDown,
  inputRef,
}: {
  label: string;
  value: string;
  setValue: Dispatch<SetStateAction<string>>;
  placeholder: string;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  onKeyDown?: (e: any) => void;
  inputRef?: any;
}) => {
  const theme = useTheme();
  return (
    <Stack flexGrow={1} pl="16px" sx={sx}>
      <InputLabel
        sx={{
          color: theme.palette.lightBg.medium,
          ...theme.typography["caption-2"],
          minWidth: "180px",
        }}
      >
        {label}
      </InputLabel>
      <InputBase
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={placeholder}
        sx={{
          ...theme.typography["body-medium"],
        }}
        autoFocus={autoFocus}
        fullWidth
        onKeyDown={onKeyDown}
        inputRef={inputRef}
      />
    </Stack>
  );
};
